import { useEffect, useRef, useState } from 'react';
import { LeafletMouseEvent } from 'leaflet';

import L from '../../../../utils/get-leaflet';

type useRectDrawProps = {
  map: L.Map | null;
  canDraw: boolean;
  onFinish: (bounds: L.LatLngBounds) => void;
  removeOnFinish?: boolean;
};

export const useRectDraw = ({
  map,
  canDraw,
  onFinish,
  removeOnFinish,
}: useRectDrawProps) => {
  const rectRef = useRef<HTMLDivElement | null>(null);
  const selectionStart = useRef<boolean>(false);

  const [rect, setRect] = useState<L.LatLngBounds | null>(null);

  type SelectionRect = {
    clientX: number;
    clientY: number;
    top: number;
    left: number;
    height: number;
    width: number;
    startCorner: L.LatLng;
    endCorner?: L.LatLng;
  };

  const selectionRect = useRef<SelectionRect>();

  useEffect(() => {
    if (!map) {
      return;
    }
    const onMouseDown = (e: LeafletMouseEvent) => {
      selectionStart.current = true;
      selectionRect.current = {
        clientX: e.originalEvent.clientX,
        clientY: e.originalEvent.clientY,
        top: e.containerPoint.y,
        left: e.containerPoint.x,
        height: 0,
        width: 0,
        startCorner: e.latlng,
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      if (
        !selectionRect.current ||
        !selectionStart.current ||
        !rectRef.current
      ) {
        return;
      }
      selectionRect.current = {
        ...selectionRect.current,
        height: e.clientY - selectionRect.current.clientY,
        width: e.clientX - selectionRect.current.clientX,
      };

      rectRef.current.style.top =
        selectionRect.current.top +
        (selectionRect.current.height < 0 ? selectionRect.current.height : 0) +
        'px';
      rectRef.current.style.left =
        selectionRect.current.left +
        (selectionRect.current.width < 0 ? selectionRect.current.width : 0) +
        'px';
      rectRef.current.style.height =
        Math.abs(selectionRect.current.height) + 'px';
      rectRef.current.style.width =
        Math.abs(selectionRect.current.width) + 'px';
    };
    const onMouseUp = () => {
      selectionStart.current = false;
      if (!rectRef.current) {
        return;
      }

      if (removeOnFinish) {
        rectRef.current.style.top = '0px';
        rectRef.current.style.left = '0px';
        rectRef.current.style.height = '0px';
        rectRef.current.style.width = '0px';
      }

      if (!selectionRect.current) {
        return;
      }

      const startCorner = map.containerPointToLatLng([
        selectionRect.current.left,
        selectionRect.current.top,
      ]);
      const endCorner = map.containerPointToLatLng([
        selectionRect.current.left + selectionRect.current.width,
        selectionRect.current.top + selectionRect.current.height,
      ]);

      const selectingBounds = L.latLngBounds(
        new L.LatLng(
          Math.min(startCorner.lat, endCorner.lat),
          Math.min(startCorner.lng, endCorner.lng)
        ),
        new L.LatLng(
          Math.max(startCorner.lat, endCorner.lat),
          Math.max(startCorner.lng, endCorner.lng)
        )
      );

      setRect(selectingBounds);

      onFinish(selectingBounds);
    };

    if (canDraw) {
      map.on('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      map.off('mousedown');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, canDraw]);

  return {
    rectRef,
    rect,
  };
};
